import React, { useState, useEffect, useLayoutEffect } from "react";
import { Row, Col, Typography } from "antd";
import { ArrowLeftOutlined, ContactsOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../hooks/auth";
import { FEATURE, ACTION } from "../../helpers/permissions";
import { useNavigationButton } from "../../pages/Layouts/Authorized/index";
import PageLayout from "../layout/PageLayout";
import SettingsPageHeader from "../layout/SettingsPageHeader";
import Card from "../card/Card";
import { Button, Toast } from "../ui";
import { DynamicForm } from "../form/DynamicForm";
import Loading from "../loading/Loading";
import ProjectService from "../../services/projectService";
import ProjectLogoUpload from "./ProjectLogoUpload";
import ProjectBudgetUpload from "./ProjectBudgetUpload";
import ProjectModules from "./ProjectModules";
import ProjectStatistics from "./ProjectStatistics";
import ProjectHelpCard from "./ProjectHelpCard";
import ProjectParameters from "./ProjectParameters";

const { Text } = Typography;

const ProjectSettings = ({
  project,
  onBack,
  onSave,
  onDelete,
  visible = true,
  allProjects = [],
}) => {
  const { theme } = useTheme();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { projectId } = useParams();

  // Detectar se é chamado pela rota ou como componente
  // Se tem projectId da URL, é rota. Se só tem project prop, é componente
  const isRouteMode = !!projectId;

  // Verificar se deve mostrar navegação no header
  // Sempre mostrar quando há projeto (independente de ser rota ou componente)
  const shouldShowHeaderNavigation = !!(project || projectId);

  // Sempre chamar o hook (regra dos hooks)
  const { setNavigationButton } = useNavigationButton();

  const [loading, setLoading] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingOrcamento, setUploadingOrcamento] = useState(false);
  const [currentProject, setCurrentProject] = useState(project);
  const [allProjectsData, setAllProjectsData] = useState(allProjects);
  const [formData, setFormData] = useState({
    companyName: "",
    description: "",
    cnpj: "",
    email: "",
    phone: "",
    type: "",
  });

  // Função para carregar projeto por ID
  const loadProjectById = async (id) => {
    try {
      setLoadingProject(true);

      const response = await ProjectService.getById(id);

      // Mapear dados do backend para frontend
      const mappedProject = ProjectService.mapToFrontend(
        response.data.response
      );

      setCurrentProject(mappedProject);

      // Se o projeto tem sincronizações, elas já vêm incluídas do backend
      if (mappedProject.synchronizations) {
      }
    } catch (error) {
      console.error("❌ Erro ao carregar projeto:", error);
      Toast.error("Erro ao carregar dados do projeto");
      // Redirecionar para a lista de projetos se não encontrar
      navigate("/projects");
    } finally {
      setLoadingProject(false);
    }
  };

  // Carregar projeto quando estiver no modo rota
  useEffect(() => {
    if (isRouteMode && projectId) {
      loadProjectById(projectId);
    }
  }, [isRouteMode, projectId]);

  // Configurar botão de voltar no header apenas quando usado como COMPONENTE
  useLayoutEffect(() => {
    // Só configurar se NÃO é modo rota (ou seja, é componente) e tem projeto
    if (!isRouteMode && project && setNavigationButton && typeof onBack === "function") {
      const backButton = (
        <Button
          onClick={onBack}
          style={{
            background: theme.background,
            border: "none",
            color: theme.primary,
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: "14px",
          }}
        >
          <ArrowLeftOutlined /> {project?.isNew ? "Voltar" : "Página do projeto"}
        </Button>
      );

      setNavigationButton(backButton);

      // Cleanup ao desmontar componente
      return () => setNavigationButton(null);
    }
    // Se é modo rota, o layout Authorized gerencia automaticamente
  }, [isRouteMode, project, setNavigationButton, onBack]); // Dependências mínimas

  useEffect(() => {
    const projectToUse = currentProject || project;
    if (projectToUse) {
      setFormData({
        companyName: projectToUse?.name || "",
        description: projectToUse?.description || "",
        cnpj: projectToUse?.cnpj || projectToUse?.companyInfo?.cnpj || "",
        email: projectToUse?.email || projectToUse?.companyInfo?.email || "",
        phone: projectToUse?.phone || projectToUse?.companyInfo?.phone || "",
        type: projectToUse?.type || projectToUse?.companyInfo?.type || "",
      });
    }
  }, [currentProject, project]);

  // Atualizar referência ao projeto atual
  const projectToUse = currentProject || project;

  // Mostrar loading se estiver carregando projeto
  if (loadingProject) {
    return isRouteMode ? (
      <PageLayout>
        <Loading />
      </PageLayout>
    ) : (
      <Loading />
    );
  }

  // Validar se tem projeto para trabalhar
  if (!visible || !projectToUse) {
    if (isRouteMode && !loadingProject) {
      // Se é modo rota e não está carregando, mas não tem projeto, é erro
      return (
        <PageLayout>
          <div style={{ textAlign: "center", padding: "50px" }}>
            <h3>Projeto não encontrado</h3>
            <Button onClick={() => navigate("/projects")}>
              Voltar para projetos
            </Button>
          </div>
        </PageLayout>
      );
    }
    return null;
  }

  const handleSaveConfig = async () => {
    try {
      if (!formData.companyName || formData.companyName.trim() === "") {
        Toast.error("O nome da empresa é obrigatório");
        return;
      }

      setLoading(true);

      const updatedProject = {
        ...projectToUse,
        name: formData.companyName || projectToUse?.name,
        description: formData.description || projectToUse?.description,
        logoUrl: currentProject?.logoUrl || projectToUse?.logoUrl,
        type: formData.type || projectToUse?.type,
        cnpj: formData.cnpj || projectToUse?.cnpj || "",
        email: formData.email || projectToUse?.email || "",
        phone: formData.phone || projectToUse?.phone || "",
        companyInfo: {
          ...projectToUse?.companyInfo,
          name: formData.companyName,
          description: formData.description,
          cnpj: formData.cnpj,
          email: formData.email,
          phone: formData.phone,
          type: formData.type,
        },
      };

      if (isRouteMode) {
        // Salvar via API diretamente
        const backendData = ProjectService.mapToBackend(updatedProject);
        await ProjectService.upsert(backendData);
        Toast.success("Configurações salvas com sucesso!");
        // Recarregar projeto
        await loadProjectById(projectId);
      } else {
        await onSave(updatedProject);
      }
    } catch (error) {
      console.error("❌ Erro ao salvar configurações:", error);
      Toast.error("Erro ao salvar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;

    try {
      setUploadingLogo(true);

      if (projectToUse?.isNew) {
        Toast.warning("Salve o projeto primeiro antes de fazer upload do logo");
        return;
      }

      if (typeof file === "string") {
        setCurrentProject((prev) => ({
          ...(prev || projectToUse),
          logoUrl: file,
        }));

        if (!isRouteMode) {
          projectToUse.logoUrl = file;
        }

        setFormData((prev) => ({ ...prev }));
        return;
      }

      const isFileLike = (
        (typeof File !== "undefined" && file instanceof File) ||
        (typeof Blob !== "undefined" && file instanceof Blob)
      );

      if (!isFileLike) {
        return;
      }

      const response = await ProjectService.uploadLogo(projectToUse?.id, file);

      if (response?.data) {
        Toast.success("Logo enviado com sucesso!");
        // Atualizar o logoUrl do projeto com a URL do Azure
        if (isRouteMode) {
          setCurrentProject((prev) => ({ ...prev, logoUrl: response.data }));
        } else {
          projectToUse.logoUrl = response.data;
        }

        // Forçar re-render para mostrar a nova imagem
        setFormData((prev) => ({ ...prev })); // Trigger re-render
      }
    } catch (error) {
      console.error("❌ Erro ao fazer upload do logo:", error);
      Toast.error("Erro ao enviar logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleOrcamentoUpload = async (files) => {
    if (!files || files.length === 0) return;

    try {
      setUploadingOrcamento(true);
      const file = files[0];

      if (projectToUse?.isNew) {
        Toast.warning(
          "Salve o projeto primeiro antes de fazer upload da planilha"
        );
        return;
      }

      const response = await ProjectService.uploadOrcamento(
        projectToUse?.id,
        file
      );

      if (response?.data) {
        Toast.success("Planilha enviada com sucesso!");
        // Atualizar o arquivo de orçamento do projeto com a URL do Azure
        if (isRouteMode) {
          setCurrentProject((prev) => ({
            ...prev,
            fileOrcamento: response.data,
          }));
        } else {
          projectToUse.fileOrcamento = response.data;
        }
      }
    } catch (error) {
      console.error("❌ Erro ao fazer upload da planilha:", error);
      Toast.error("Erro ao enviar planilha");
    } finally {
      setUploadingOrcamento(false);
    }
  };

  const handleModuleClick = (moduleKey) => {
    if (!projectToUse?.id) {
      Toast.warning("Salve o projeto primeiro antes de acessar os módulos");
      return;
    }

    // Navegar para a página de integrações ERP
    if (moduleKey === "sync") {
      navigate(`/projects/${projectToUse.id}/integrations`);
    }
    // Navegar para a página de relatórios com filtro do projeto
    else if (moduleKey === "reports") {
      navigate(`/reports?projectId=${projectToUse.id}`);
    }
    // Outros módulos podem ser implementados aqui no futuro
  };

  // Handler para voltar (apenas quando usado como componente)
  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  // Extrair tipos únicos e criar opções
  const projectsForTypes =
    allProjectsData.length > 0 ? allProjectsData : allProjects;
  const existingTypes = ProjectService.extractUniqueTypes(projectsForTypes);
  const defaultTypes = [
    "E-commerce",
    "Serviços",
    "Indústria",
    "Varejo",
    "Saúde",
    "Educação",
    "Tecnologia",
    "Alimentação",
    "Construção",
    "Consultoria",
  ];
  const typeOptions =
    existingTypes.length > 0
      ? existingTypes.map((type) => ({ label: type, value: type }))
      : defaultTypes.map((type) => ({ label: type, value: type }));

  // Configuração do formulário
  const companyFormConfig = [
    {
      columns: 1,
      questions: [
        {
          id: "companyName",
          label: "Nome do Projeto",
          type: "text",
          placeholder: "Digite o nome do projeto",
          required: true,
        },
        {
          id: "cnpj",
          label: "CNPJ",
          type: "cnpj",
          placeholder: "00.000.000/0001-00",
        },
      ],
    },
    {
      columns: 2,
      questions: [
        {
          id: "email",
          label: "Email",
          type: "email",
          placeholder: "contato@empresa.com",
        },
        {
          id: "phone",
          label: "Telefone",
          type: "phone",
          placeholder: "(11) 99999-9999",
        },
      ],
    },
  ];

  const content = (
    <div>
      <div
        style={{
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
          overflowX: "hidden",
          ...(projectToUse?.isNew
            ? {
                maxWidth: 1040,
                padding: 16,
                borderRadius: 12,
                backgroundColor:
                  theme.backgroundSecondary ||
                  (theme.primary ? `${theme.primary}0A` : theme.background),
                border: `1px solid ${theme.borderColor || theme.border}`,
              }
            : {}),
        }}
      >
        <SettingsPageHeader
          topLeft={
            !isRouteMode && projectToUse?.isNew && typeof onBack === "function" ? (
              <Button
                onClick={onBack}
                style={{
                  background: theme.background,
                  border: "none",
                  color: theme.primary,
                  cursor: "pointer",
                  padding: "4px 8px",
                  fontSize: "14px",
                }}
              >
                <ArrowLeftOutlined /> Voltar
              </Button>
            ) : null
          }
          title={projectToUse?.isNew ? "Novo Projeto" : "Configurações do Projeto"}
          subtitle={
            projectToUse?.isNew
              ? "Configure as informações do seu novo projeto"
              : "Gerencie as configurações gerais do seu projeto e acesso os módulos"
          }
          actions={
            <Button
              type="primary"
              icon={<ContactsOutlined />}
              onClick={handleSaveConfig}
              loading={loading}
              style={{
                backgroundColor: theme.primary,
                borderColor: theme.primary,
                borderRadius: 8,
                height: 40,
                fontSize: 14,
              }}
              data-tour-id="project-save-btn"
            >
              {projectToUse?.isNew ? "Criar Projeto" : "Salvar Configurações"}
            </Button>
          }
        />

        <Row gutter={[24, 24]}>
          {/* Coluna Esquerda - Informações da Empresa */}
          <Col xs={24} lg={24}>
            <Card
              title={
                <span style={{ fontSize: 16, fontWeight: 600 }}>
                  Informações do Projeto
                </span>
              }
              style={{ marginBottom: 24 }}
            >
              {/* Logo da Empresa */}
              <div style={{ marginBottom: 24 }} data-tour-id="project-logo-upload">
                <Text
                  style={{
                    color: theme.textSecondary,
                    fontSize: 14,
                    fontWeight: 500,
                    display: "block",
                    marginBottom: 12,
                  }}
                >
                  Logo do Projeto
                </Text>

                <ProjectLogoUpload
                  project={projectToUse}
                  loading={uploadingLogo}
                  onUpload={handleLogoUpload}
                  isNewProject={projectToUse?.isNew}
                  
                />
              </div>

              {/* Formulário */}
              <div data-tour-id="project-form">
                <DynamicForm
                  formConfig={companyFormConfig}
                  values={formData}
                  setValues={setFormData}
                  submitText={null}
                  onSubmit={null}
                  inputPadding="8px 12px"
                />
              </div>

              {/* Planilha de Orçamento */}
              {/*<div style={{ marginTop: 24 }}>
                <Text style={{ 
                  color: theme.textSecondary, 
                  fontSize: 14,
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 12
                }}>
                  Planilha de Orçamento
                </Text>
                
                <ProjectBudgetUpload
                  project={project}
                  loading={uploadingOrcamento}
                  onUpload={handleOrcamentoUpload}
                  isNewProject={project.isNew}
                />
              </div>
              */}
            </Card>

            {/* Configuração de Parâmetros da Empresa */}
            {/*<ProjectParameters onParametersClick={handleParametersClick} />*/}
          </Col>
        </Row>
      </div>
    </div>
  );

  // Retornar com PageLayout se for modo rota, senão retornar conteúdo direto
  return isRouteMode ? <PageLayout>{content}</PageLayout> : content;
};

export default ProjectSettings;
